import { type FC } from 'react';
import type { RatingProps } from '@packages/shared';
import { useIntl } from 'react-intl';
import { Rating } from '@packages/shared/src/components/Rating';
import type { StackProps } from '@packages/shared/src/components/Stack';
import { Stack } from '@packages/shared/src/components/Stack';
import { ratingIconsMessages } from './messages';

export type RatingIconsProps = {
  /**
   * Average rating for a given product, ranging from 0 to 5 with a precision of 0.1.
   * */
  avgRating: RatingProps['value'];
  /**
   * Amount of given ratings.
   * */
  reviewCount: number;
  /**
   *The size of the component.
   *
   * @default medium
   */
  size?: RatingProps['size'];
} & StackProps;

/**
 * Shows RatingIcons and the amount of given ratings
 * */
export const RatingIcons: FC<RatingIconsProps> = ({
  avgRating,
  reviewCount,
  size = 'medium',
  ...stackProps
}) => {
  const { formatMessage } = useIntl();

  const maxRating = 5;

  const ariaLabelText = formatMessage(
    avgRating && reviewCount
      ? ratingIconsMessages.ratingAriaLabel
      : ratingIconsMessages.ratingAriaLabelZero,
    { avgRating: avgRating?.toFixed(2), maxRating, reviewCount },
  );

  return (
    <Stack
      aria-label={ariaLabelText}
      direction="row"
      alignItems="flex-end"
      gap={0.5}
      {...stackProps}
    >
      <Rating
        readOnly
        size={size}
        name="customized-color"
        value={avgRating}
        defaultValue={0}
        precision={0.1}
      />
      {reviewCount > 0 && (
        <span
          className={`text-color-text-dark text-size-body3 leading-[1.27rem] ${typeof stackProps?.onClick === 'function' ? 'underline' : 'no-underline'}`}
        >
          ({reviewCount})
        </span>
      )}
    </Stack>
  );
};
